import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Crown, Zap, Trophy, User, Settings, LogOut, ArrowLeft, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useState } from "react";

const TIER_CONFIG = {
  free: {
    name: "Free",
    icon: User,
    color: "bg-muted text-muted-foreground",
    features: ["View game scores", "Basic standings", "Limited player tracking (3)"],
  },
  light: {
    name: "Light",
    icon: Zap,
    color: "bg-blue-500/10 text-blue-500",
    features: ["Full Elite access for first 5 days", "Basic team statistics", "Track up to 8 players"],
  },
  pro: {
    name: "Pro",
    icon: Crown,
    color: "bg-amber-500/10 text-amber-500",
    features: ["Full Elite access for first 5 days", "Player profiles", "Playoff predictions", "Track up to 8 players"],
  },
  elite: {
    name: "Elite",
    icon: Trophy,
    color: "bg-primary/10 text-primary",
    features: ["Everything in Pro", "Advanced analytics", "Full betting odds access", "Track up to 24 players", "Priority support"],
  },
};

export default function Account() {
  const { user, session, subscription, isLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    if (!isLoading && !user) {
      navigate("/auth?redirect=/account");
    }
  }, [user, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const currentTier = subscription.subscribed ? subscription.tier : "free";
  const tierConfig = TIER_CONFIG[currentTier || "free"];
  const TierIcon = tierConfig.icon;

  const handleManageSubscription = async () => {
    if (!session) return;

    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error: any) {
      console.error("Portal error:", error);
      toast.error(error.message || "Failed to open subscription portal");
    } finally {
      setPortalLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
    toast.success("Signed out successfully");
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Account</h1>
            <p className="text-sm text-muted-foreground">Manage your profile and subscription</p>
          </div>
        </div>

        {/* Profile Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground">Email</label>
              <p className="text-foreground font-medium">{user.email}</p>
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Member since</label>
              <p className="text-foreground font-medium">
                {new Date(user.created_at).toLocaleDateString()}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Subscription
            </CardTitle>
            <CardDescription>Your current plan and billing</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Current Tier */}
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${tierConfig.color}`}>
                <TierIcon className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-display font-bold text-lg text-foreground">
                    {tierConfig.name}
                  </span>
                  {subscription.isTrial && (
                    <Badge variant="secondary" className="text-xs">Trial</Badge>
                  )}
                  {subscription.isFullAccessPeriod && subscription.tier !== "elite" && (
                    <Badge variant="outline" className="text-xs text-primary border-primary">
                      Elite Access
                    </Badge>
                  )}
                </div>
                {subscription.subscriptionEnd && (
                  <p className="text-sm text-muted-foreground">
                    {subscription.isTrial ? "Trial ends" : "Renews"}{" "}
                    {new Date(subscription.subscriptionEnd).toLocaleDateString()}
                  </p>
                )}
                {!subscription.subscribed && (
                  <p className="text-sm text-muted-foreground">Free tier</p>
                )}
              </div>
            </div>

            {/* Features */}
            <div className="bg-secondary/50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-foreground mb-2">Your features:</h4>
              <ul className="space-y-1">
                {tierConfig.features.map((feature) => (
                  <li key={feature} className="text-sm text-muted-foreground flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              {subscription.subscribed ? (
                <Button
                  onClick={handleManageSubscription}
                  disabled={portalLoading}
                  className="gap-2"
                >
                  {portalLoading ? "Loading..." : "Manage Subscription"}
                  <ExternalLink className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  onClick={() => navigate("/?openSubscription=true")}
                  className="gap-2"
                >
                  <Crown className="w-4 h-4" />
                  Upgrade to Pro
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Sign Out */}
        <Card>
          <CardContent className="pt-6">
            <Button variant="destructive" onClick={handleSignOut} className="w-full gap-2">
              <LogOut className="w-4 h-4" />
              Sign Out
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
